import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import moment from "moment";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
    BondOrderHistoryResponse,
    isExternalBondOrder,
    isInternalOrderDeleted,
    usesExternalDeleteApi,
} from "../Users/Orders/helpers";
import { makeGETRequestOnUserService } from "../../Api/api";
import { userServiceAxiosInstance } from "../../Api/setUserServiceToken";
import {
    getOrderTimestamp,
    is_gsec_sdl_tBill,
    toINR,
} from "../../Helpers/functions";
import SummeryCard from "../Users/Orders/SummeryCard";
import DesktopView from "../Users/Orders/DesktopView";
import MobileView from "../Users/Orders/MobileView";
import toastr from "toastr";

const getOrderDeletedMoment = (order: BondOrderHistoryResponse) => {
    if (!isInternalOrderDeleted(order)) return null;
    const deletedAt = moment.utc(order.order_deleted_date);
    return deletedAt.isValid() ? deletedAt : null;
};

const filterCashflowsByDeletedDate = (order: BondOrderHistoryResponse) => {
    const cashflows = Array.isArray(order.cashflows) ? order.cashflows : [];
    if (!isInternalOrderDeleted(order)) {
        return cashflows;
    }
    const deletedAt = getOrderDeletedMoment(order);
    if (!deletedAt) return cashflows;
    return cashflows.filter((cf) => {
        const cfDate = moment.utc(cf.date);
        return cfDate.isValid() && cfDate.isSameOrBefore(deletedAt, "day");
    });
};

const hasRelevantCashflows = (
    cashflows: BondOrderHistoryResponse["cashflows"]
) =>
    Array.isArray(cashflows) &&
    cashflows.some((cf) => cf.type === "Interest" || cf.type === "Maturity");

export const PortfolioDetails = ({ currentUser, user_type }: any) => {
    const [bondOrders, setBondOrders] = useState<BondOrderHistoryResponse[]>([]);
    const [openMain, setOpenMain] = useState<{ [key: string]: boolean }>({});
    const [openYear, setOpenYear] = useState<{
        [key: string]: { [year: string]: boolean };
    }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [orderPendingDeletion, setOrderPendingDeletion] =
        useState<BondOrderHistoryResponse | null>(null);
    const [deletingOrderUuid, setDeletingOrderUuid] = useState<string | null>(
        null
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const fetchBondOrders = useCallback(async () => {
        if (!currentUser.uuid) return;
        setIsLoading(true);
        try {
            const [ordersResp] = await Promise.allSettled([
                makeGETRequestOnUserService(
                    `order_management_v2/orders/history/${currentUser.uuid}/?type=${user_type}`
                ),
            ]);

            const orders =
                ordersResp.status === "fulfilled" && ordersResp.value?.status === 200
                    ? ordersResp.value.data
                    : [];

            const combined = [...(orders || [])];
            combined.sort((a, b) => getOrderTimestamp(b) - getOrderTimestamp(a));
            setBondOrders(combined);
        } catch (error) {
            toastr.error("Failed to fetch bond orders");
        } finally {
            setIsLoading(false);
        }
    }, [currentUser.uuid]);

    useEffect(() => {
        fetchBondOrders();
    }, [fetchBondOrders]);

    const handleRequestDeleteOrder = useCallback(
        (order: BondOrderHistoryResponse) => {
            if (!order?.uuid || deletingOrderUuid) return;
            setOrderPendingDeletion(order);
        },
        [deletingOrderUuid]
    );

    const handleCloseDeleteDialog = useCallback(() => {
        if (deletingOrderUuid) return;
        setOrderPendingDeletion(null);
    }, [deletingOrderUuid]);

    const confirmDeleteOrder = useCallback(async () => {
        if (!orderPendingDeletion?.uuid) return;
        setDeletingOrderUuid(orderPendingDeletion.uuid);
        const isExternalDelete = usesExternalDeleteApi(orderPendingDeletion);
        const orderType = isExternalDelete ? "external" : "internal";
        try {
            await userServiceAxiosInstance.delete(
                `/partneruser/partner_client_orders_delete/?order=${orderType}&order_uuid=${encodeURIComponent(
                    orderPendingDeletion.uuid
                )}&order_delete=true`
            );
            toastr.success(
                isExternalDelete ? "External bond removed." : "Internal bond removed."
            );
            await fetchBondOrders();
        } catch (_error) {
            toastr.error(
                isExternalDelete
                    ? "Unable to remove external bond"
                    : "Unable to remove internal bond"
            );
        } finally {
            setDeletingOrderUuid(null);
            setOrderPendingDeletion(null);
        }
    }, [fetchBondOrders, orderPendingDeletion]);

    const settledOrders = useMemo(
        () =>
            bondOrders.filter(
                (order) =>
                    order.deal_status === "Settled" || isExternalBondOrder(order)
            ),
        [bondOrders]
    );

    const activeOrders = useMemo(
        () => settledOrders.filter((order) => !isInternalOrderDeleted(order)),
        [settledOrders]
    );

    const cashflowOrders = useMemo(() => {
        return settledOrders
            .map((order) => {
                if (isInternalOrderDeleted(order)) {
                    const trimmedCashflows = filterCashflowsByDeletedDate(order);
                    if (!hasRelevantCashflows(trimmedCashflows)) {
                        return null;
                    }
                    return { ...order, cashflows: trimmedCashflows };
                }
                return order;
            })
            .filter(Boolean) as BondOrderHistoryResponse[];
    }, [settledOrders]);

    const portfolioOrders = useMemo(
        () =>
            [...cashflowOrders].sort(
                (a, b) => getOrderTimestamp(b) - getOrderTimestamp(a)
            ),
        [cashflowOrders]
    );

    const aggregatedCashflows = useMemo(() => {
        if (!portfolioOrders) return {};
        return portfolioOrders.reduce((acc, order) => {
            const grouping: { [year: string]: any } = {};
            (order.cashflows || []).forEach((cf) => {
                const yr = moment(cf.date).format("YYYY");
                if (!grouping[yr])
                    grouping[yr] = {
                        year: yr,
                        frequency_of_interest: order.frequency_of_interest,
                        interest: 0,
                        interestAfterTax: 0,
                        maturity: 0,
                        dateMap: {},
                    };
                const entry = grouping[yr];
                if (!entry.dateMap[cf.date]) {
                    entry.dateMap[cf.date] = {
                        date: cf.date,
                        interest: 0,
                        interestAfterTax: 0,
                        maturity: 0,
                    };
                }
                if (cf.type === "Interest") {
                    entry.interest += Number(cf.amount);
                    const afterTax =
                        !is_gsec_sdl_tBill(order.security_type) &&
                            order.tax_category !== "Tax Free"
                            ? Number(cf.amount) * 0.9
                            : Number(cf.amount);
                    entry.interestAfterTax += afterTax;
                    entry.dateMap[cf.date].interest += Number(cf.amount);
                    entry.dateMap[cf.date].interestAfterTax += afterTax;
                }
                if (cf.type === "Maturity") {
                    entry.maturity += Number(cf.amount);
                    entry.dateMap[cf.date].maturity += Number(cf.amount);
                }
            });
            acc[order.uuid] = Object.values(grouping);
            return acc;
        }, {} as Record<string, any[]>);
    }, [portfolioOrders]);

    const totalData = useMemo(() => {
        const totalInterestEarned = cashflowOrders
            ?.flatMap((order) =>
                (order.cashflows || [])
                    .filter(
                        (cashflow) =>
                            cashflow.type === "Interest" &&
                            moment(cashflow.date).isBefore(moment().endOf("day"))
                    )
                    .map((cashflow) => Number(cashflow.amount))
            )
            .reduce((acc, amount) => acc + amount, 0);

        const bondInvestment = activeOrders?.reduce((total, order) => {
            const consideration = Number(order.total_consideration) || 0;
            const stampDuty = Number(order.stamp_duty ?? 0) || 0;
            return total + consideration + stampDuty;
        }, 0);

        const totalInvestment = bondInvestment || 0;

        const totalSellYield = activeOrders.reduce((total, order) => {
            return total + parseFloat(order.sell_yield);
        }, 0);

        const yieldToMaturity = activeOrders.length
            ? totalSellYield / activeOrders.length
            : 0;

        return [
            {
                label: "Total Investments",
                info: "Total Amount Invested",
                value:
                    totalInvestment !== undefined ? (
                        toINR(totalInvestment, 2)
                    ) : (
                        <Skeleton variant="rounded" width={200} height={40} />
                    ),
            },
            {
                label: "Weighted Average Yield",
                info: "Average Yield across your investments",
                value:
                    yieldToMaturity !== 0 ? (
                        `${yieldToMaturity.toFixed(2)}%`
                    ) : (
                        <Skeleton variant="rounded" width={200} height={40} />
                    ),
            },
            {
                label: "Total Interest earned",
                info: "Total Interest earned across all your investments",
                value:
                    totalInterestEarned !== undefined ? (
                        toINR(totalInterestEarned, 2)
                    ) : (
                        <Skeleton variant="rounded" width={200} height={40} />
                    ),
            },
        ];
    }, [activeOrders, cashflowOrders]);

    const deleteDialogTitle = orderPendingDeletion
        ? usesExternalDeleteApi(orderPendingDeletion)
            ? "Remove External Bond?"
            : "Remove Internal Bond?"
        : "Remove Bond?";

    if (isLoading && !portfolioOrders.length) {
        return (
            <Box sx={{ textAlign: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: "90%", mx: "auto", zoom: 0.9 }}>
            <Box sx={{ mb: 2, mx: [2, 4] }}>
                <Grid container spacing={[1, 2]}>
                    {totalData.map((item, index) => {
                        return <SummeryCard key={index} item={item} index={index} />;
                    })}
                </Grid>
            </Box>
            <Box sx={{ mb: 4, mx: [2, 4] }}>
                {!isMobile ? (
                    <TableContainer>
                        <Table sx={{ borderCollapse: "separate", borderSpacing: "0 8px" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Security Name</TableCell>
                                    <TableCell>Security Type</TableCell>
                                    <TableCell>Coupon</TableCell>
                                    <TableCell>Interest Frequency</TableCell>
                                    <TableCell>Date (Type)</TableCell>
                                    <TableCell>Rating</TableCell>
                                    <TableCell>Qty</TableCell>
                                    <TableCell>Yield</TableCell>
                                    <TableCell>Investment Amount</TableCell>
                                    <TableCell>Cashflows</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {portfolioOrders.map((order) => (
                                    <DesktopView
                                        key={order.uuid}
                                        order={order}
                                        aggregatedCashflows={aggregatedCashflows}
                                        openMain={openMain}
                                        openYear={openYear}
                                        setOpenMain={setOpenMain}
                                        setOpenYear={setOpenYear}
                                        onDeleteOrder={handleRequestDeleteOrder}
                                        deletingOrderUuid={deletingOrderUuid}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box>
                        {portfolioOrders.map((order) => (
                            <MobileView
                                key={order.uuid}
                                order={order}
                                aggregatedCashflows={aggregatedCashflows}
                                openMain={openMain}
                                openYear={openYear}
                                setOpenMain={setOpenMain}
                                setOpenYear={setOpenYear}
                                onDeleteOrder={handleRequestDeleteOrder}
                                deletingOrderUuid={deletingOrderUuid}
                            />
                        ))}
                    </Box>
                )}
            </Box>
            <Dialog
                open={!!orderPendingDeletion}
                onClose={handleCloseDeleteDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>{deleteDialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Are you sure you want to remove{" "}
                        <Typography component="span" sx={{ fontWeight: 700 }}>
                            {orderPendingDeletion?.security_name || "this bond"}
                        </Typography>{" "}
                        from the portfolio? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        disabled={!!deletingOrderUuid}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDeleteOrder}
                        disabled={!!deletingOrderUuid}
                    >
                        {deletingOrderUuid ? (
                            <CircularProgress size={18} sx={{ color: "inherit" }} />
                        ) : (
                            "Remove"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
